import { Drawer, IconButton, Portal } from '@mui/material';
import { FunctionComponent, useEffect, useState } from 'react';
import { Close } from '@mui/icons-material';
import LoadingBlock from '@/components/feedback/LoadingBlock';
import { useTranslation } from '@/lib';
import { useExportToExcel } from '@/hooks/useExportToExcel';
import dayjs from 'dayjs';
import { BuildupGrid, BuildupDataModel } from './BuildupGrid';
import { useApiCall } from '@/hooks/useApiCall';
import { balanceService } from '../api/balances/balance.service';
import { locationService } from '@/modules/locations/api/locations/location.service';
import { LocationCounterpartBalanceDTO } from '../api/balances/balance.contracts';
import { DisplayMode } from '@/types/display-mode.enum';
import { GridApi } from 'ag-grid-community';

interface LocationBalanceBuildupDrawerProps {
  locationId: number;
  assetTypeId: number;
  assetTypeName: string;
  assetTypeCode: string;
  isOpen: boolean;
  onClose: () => void;
  displayMode: DisplayMode;
  isGuestView?: boolean;
}

const LocationBalanceBuildupDrawer: FunctionComponent<LocationBalanceBuildupDrawerProps> = ({
  locationId,
  assetTypeId,
  assetTypeName,
  assetTypeCode,
  isOpen,
  onClose,
  displayMode,
  isGuestView = false,
}) => {
  const { t } = useTranslation();
  const { exportToExcel } = useExportToExcel();
  const [buildupData, setBuildupData] = useState<BuildupDataModel[]>([]);

  const withHierarchical = displayMode === DisplayMode.HIERARCHICAL;

  const { data, isLoading, isError, setApiCallArg } = useApiCall<LocationCounterpartBalanceDTO[]>(() =>
    balanceService.getLocationCounterpartBalance({ locationId, assetTypeId, withHierarchical }),
  );

  const { data: locationData, setApiCallArg: setLocationApiCallArg } = useApiCall(() => locationService.getById(locationId));

  useEffect(() => {
    if (isOpen) {
      setApiCallArg(() => balanceService.getLocationCounterpartBalance({ locationId, assetTypeId, withHierarchical }));
      setLocationApiCallArg(() => locationService.getById(locationId));
    }
  }, [locationId, assetTypeId, isOpen, setApiCallArg, setLocationApiCallArg, withHierarchical]);

  useEffect(() => {
    // Transform API data to BuildupDataModel
    if (data && Array.isArray(data)) {
      setBuildupData(
        data.map((item) => ({
          id: item.location.id,
          counterpartLocationName: item.location.name,
          counterpartLocationCode: item.location.code,
          balance: item.balance,
        })),
      );
    } else {
      setBuildupData([]);
    }
  }, [data]);

  const handleExportToExcel = (gridApi: GridApi<BuildupDataModel> | undefined) => {
    if (gridApi) {
      const rowData: BuildupDataModel[] = [];
      gridApi.forEachNode((node) => {
        if (node.data) {
          rowData.push(node.data);
        }
      });

      if (rowData.length > 0) {
        const locationPart = locationData?.dto ? `${locationData.dto.name}` : `location_${locationId}`;
        exportToExcel(
          rowData.map((item) => ({
            Location: locationPart,
            AssetType: `${assetTypeName}`,
            CounterpartLocation: `${item.counterpartLocationName}`,
            Debit: item.balance < 0 ? Math.abs(item.balance) : '',
            Credit: item.balance > 0 ? item.balance : '',
          })),
          `buildup_${locationPart}_${assetTypeCode}_${dayjs().format('YYYY_MM_DD_HHmmss')}.xlsx`,
        );
      }
    }
  };

  return (
    <Portal>
      <Drawer anchor={'right'} open={isOpen} hideBackdrop variant="persistent">
        <div className="flex h-full w-[768px] flex-col justify-between px-6 py-4">
          <div className="flex h-full flex-col">
            {isLoading ? (
              <LoadingBlock />
            ) : (
              <>
                {/* header */}
                <div className="div flex items-center justify-between mb-4">
                  <div className="flex flex-col">
                    <div className="text-xs text-gray-500 dark:text-gray-400">{t('buildup')}</div>
                    <div className="text-lg font-medium">
                      {assetTypeName} ({assetTypeCode})
                    </div>
                  </div>
                  <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
                    <Close />
                  </IconButton>
                </div>
                {/* content */}
                <div className="flex h-full flex-col flex-1">
                  <BuildupGrid
                    data={buildupData}
                    isLoading={isLoading}
                    isError={isError}
                    onExportToExcelClicked={handleExportToExcel}
                    isGuestView={isGuestView}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </Drawer>
    </Portal>
  );
};

export default LocationBalanceBuildupDrawer;
